import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, TrendingUp, TrendingDown, Minus, BookOpen, FlaskConical, Leaf, ExternalLink } from "lucide-react";
import ProductPlaceholder from "@/components/ProductPlaceholder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Resolve a curated-product URL: relative `/r/<id>` paths go through the Badi
// redirector so we get UTM attribution; absolute URLs are used as-is.
function resolveProductUrl(url, memberId) {
  if (!url) return "#";
  if (url.startsWith("/r/")) {
    return `${BACKEND_URL}/api${url}${memberId ? `?m=${encodeURIComponent(memberId)}` : ""}`;
  }
  return url;
}

export default function Digest() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/digest").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0]">
        <Header />
        <p className="p-20 text-center text-[#5C6359]">Composing your read…</p>
      </div>
    );
  }
  if (!data) return null;

  const weekLabel = data.week_of ? new Date(data.week_of).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 pt-16 pb-10 max-w-3xl mx-auto" data-testid="digest-page">
        <p className="label-eyebrow mb-3">Week of {weekLabel}</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D322B]">Your weekly read.</h1>
        <p className="mt-4 text-[#5C6359] leading-relaxed max-w-xl">
          One quiet page, once a week. Your shifts, a few essays, what the science is saying, and the
          plant medicines we trust. No notifications, no urgency — meet it on your own time.
        </p>
      </section>

      {/* Massey's read */}
      {data.weekly_read && (
        <section className="px-6 sm:px-12 lg:px-20 pb-10 max-w-3xl mx-auto" data-testid="digest-massey">
          <div className="rounded-3xl bg-[#2D322B] text-[#F9F6F0] p-8">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-[#D49A44]" />
              <p className="label-eyebrow !text-[#D49A44]">Massey&apos;s read</p>
            </div>
            <p className="font-serif text-lg sm:text-xl leading-relaxed text-[#F9F6F0]/95 whitespace-pre-wrap">
              {data.weekly_read}
            </p>
          </div>
        </section>
      )}

      {/* Personal trends */}
      {data.trends && data.trends.length > 0 && (
        <section className="px-6 sm:px-12 lg:px-20 pb-12 max-w-3xl mx-auto" data-testid="digest-trends">
          <p className="label-eyebrow mb-4">This week&apos;s shifts</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.trends.map((t) => {
              const isUp = t.direction === "up";
              const Icon = isUp ? TrendingUp : t.direction === "down" ? TrendingDown : Minus;
              const z = t.zone;
              const statusTone = z?.status === "optimal"
                ? "text-[#2A3B2C]"
                : z?.status === "attention"
                  ? "text-[#8F3A3A]"
                  : z?.status === "borderline"
                    ? "text-[#8a6620]"
                    : "text-[#5C6359]";
              return (
                <div key={t.marker} className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-[#5C6359]">{t.marker}</p>
                  <p className="font-serif text-2xl text-[#2D322B] mt-1">
                    {t.current_avg} <span className="text-xs text-[#5C6359]">{t.unit}</span>
                  </p>
                  <div className={`mt-2 flex items-center gap-2 text-xs ${statusTone}`}>
                    <Icon className="w-3.5 h-3.5" />
                    <span>{t.delta_pct > 0 ? "+" : ""}{t.delta_pct}% · {z?.zone || ""}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Library */}
      <section className="px-6 sm:px-12 lg:px-20 pb-12 max-w-3xl mx-auto" data-testid="digest-library">
        <div className="flex items-center justify-between mb-4">
          <p className="label-eyebrow flex items-center gap-2"><BookOpen className="w-3.5 h-3.5 text-[#C05C42]" /> Reading</p>
          <Link to="/library" className="text-xs uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B]">All essays →</Link>
        </div>
        <div className="space-y-3">
          {data.library.map((c) => (
            <Link key={c.id} to={`/library/${c.id}`} data-testid={`digest-essay-${c.id}`} className="block bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-5 hover:-translate-y-0.5 transition-transform">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-widest text-[#C05C42]">{c.category}</p>
                <p className="text-xs text-[#5C6359]">{c.reading_time}</p>
              </div>
              <h3 className="font-serif text-xl text-[#2D322B] mt-2 leading-snug">{c.title}</h3>
              <p className="text-sm text-[#5C6359] mt-2 leading-relaxed">{c.excerpt}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Research */}
      <section className="px-6 sm:px-12 lg:px-20 pb-12 max-w-3xl mx-auto" data-testid="digest-research">
        <p className="label-eyebrow mb-4 flex items-center gap-2"><FlaskConical className="w-3.5 h-3.5 text-[#C05C42]" /> What the science is saying</p>
        <div className="space-y-3">
          {data.research.map((r) => (
            <a key={r.id} href={r.url} target="_blank" rel="noreferrer" data-testid={`digest-research-${r.id}`} className="block bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-5 hover:-translate-y-0.5 transition-transform group">
              <p className="text-xs uppercase tracking-widest text-[#C05C42]">{r.tradition}</p>
              <h3 className="font-serif text-xl text-[#2D322B] mt-2 leading-snug flex items-start gap-2">
                {r.title}
                <ExternalLink className="w-3.5 h-3.5 mt-1 text-[#5C6359] group-hover:text-[#C05C42]" />
              </h3>
              <p className="text-xs text-[#5C6359] mt-1 italic">{r.source}</p>
              <p className="text-sm text-[#5C6359] mt-2 leading-relaxed">{r.summary}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="px-6 sm:px-12 lg:px-20 pb-24 max-w-3xl mx-auto" data-testid="digest-products">
        <p className="label-eyebrow mb-4 flex items-center gap-2"><Leaf className="w-3.5 h-3.5 text-[#C05C42]" /> What we&apos;re reaching for</p>
        <p className="text-xs text-[#5C6359] mb-4 italic">
          Curated by the Badi team. Not a paid placement; we use these in practice.
        </p>
        <div className="space-y-3">
          {data.products.map((p) => (
            <a key={p.id} href={resolveProductUrl(p.url, user?.id)} target="_blank" rel="noreferrer" data-testid={`digest-product-${p.id}`} className="flex items-start gap-4 bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-4 hover:-translate-y-0.5 transition-transform group">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#D1C7B8] shrink-0">
                {p.image ? (
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <ProductPlaceholder name={p.name} category={p.category} tags={p.tags} productId={p.id} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-widest text-[#C05C42]">{p.category}</p>
                <p className="font-serif text-lg text-[#2D322B] mt-1 leading-snug flex items-center gap-2">
                  {p.name}
                  <ExternalLink className="w-3.5 h-3.5 text-[#5C6359] group-hover:text-[#C05C42]" />
                </p>
                <p className="text-sm text-[#5C6359] mt-1 leading-relaxed">{p.summary}</p>
                {p.why && (
                  <p className="text-xs text-[#C05C42] mt-2 italic">{p.why}</p>
                )}
              </div>
            </a>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
